/**
 * @description A reusable credential picker component that displays credentials 
 * in a select dropdown with an option to add new connections
 */

import { useNavigate, useLocation } from "react-router-dom";
import { Plus } from "lucide-react";
import { 
  Select, 
  SelectTrigger, 
  SelectValue, 
  SelectContent, 
  SelectItem 
} from "@strivelabs/ui-core";
import { useCredentials } from "@/hooks/useConnections";
import { ConnectionsCardData } from "@/types/connections";
import { formatDate } from "@/utils/date";

interface CredentialPickerProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  appName?: string; // Filter credentials by app name
}

export const CredentialPicker = ({
  value,
  onValueChange,
  placeholder = "Select a credential",
  className,
  disabled = false,
  appName
}: CredentialPickerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: credentialsData, isLoading } = useCredentials();

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === "add-new") {
      // Navigate to AccountSettings with state containing return information
      navigate("/ui/settings", {
        state: {
          openConnectionsModal: true,
          returnToRoute: location.pathname,
          connectionAppName: appName
        }
      });
      return;
    }
    
    if (onValueChange) {
      onValueChange(selectedValue);
    }
  };

  const credentials = (credentialsData?.credentials || []).filter((cred: ConnectionsCardData) => 
    !appName || cred.app_name === appName
  );
  
  // Find the selected credential to show simplified display in SelectValue
  const selectedCredential = credentials.find((cred: ConnectionsCardData) => cred.id === value);
  const selectedDisplayText = selectedCredential 
    ? `${selectedCredential.display_name} - ${selectedCredential.principal_name || selectedCredential.principal_email}`
    : '';

  return (
    <Select 
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled || isLoading}
    >
      <SelectTrigger 
        className={`w-full ${className || ""}`}
      >
        <SelectValue 
          placeholder={
            isLoading 
              ? "Loading credentials..." 
              : credentials.length === 0 
                ? "No credentials available" 
                : placeholder
          }
        >
          {selectedCredential && credentials.length > 0 && (
            <span className="truncate">
              {selectedDisplayText}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-white border-gray-200 text-gray-900">
        {credentials.length === 0 && !isLoading && (
          <div className="px-3 py-2 text-sm text-gray-500 text-center">
            No credentials available
          </div>
        )}
        
        {credentials.map((credential: ConnectionsCardData) => (
          <SelectItem
            key={credential.id}
            value={credential.id}
            className="cursor-pointer hover:bg-gray-100"
          >
            <div className="flex flex-col">
              <span className="font-medium">
                {credential.display_name} - {credential.principal_name || credential.principal_email}
              </span>
              <span className="text-xs text-gray-500">
                {credential.app_name} • {credential.created_at ? formatDate(new Date(credential.created_at)) : 'Recently added'}
              </span>
            </div>
          </SelectItem>
        ))}
        
        <div className="border-t border-gray-100 my-1" />
        <SelectItem
          value="add-new"
          className="cursor-pointer hover:bg-gray-100 custom-text-primary font-medium"
        >
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            <span>Add Connection</span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
};
